import { PlusOutlined } from "@ant-design/icons";
import { Image, Upload } from "antd";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { MAX_FILE_SIZE } from "../../config/Constant";
import { useInfoUser } from "../../store/UserStore";
import { openNotificationError } from "../../utils/Utils";
import "./style.scss";
import { UploadedFilePreview } from "./UploadedFilePreview";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const MailFilesPreview = ({
  previewFiles,
  setPreviewFiles,
  setIsOpenUploadModal,
  setUploadFiles,
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const { languageMap, user } = useInfoUser();

  const handlePreview = async (file) => {
    if (file?.mainPath) {
      setPreviewImage(file.mainPath);
      setPreviewOpen(true);
      return;
    }

    if (!file?.url && !file?.preview) {
      file.preview = await getBase64(file?.originFileObj);
    }

    setPreviewImage(file?.url || file?.preview);
    setPreviewOpen(true);
  };

  /**
   * Why have to use debounce when click to add files
   * on antd upload component, onChange will be triggered with each file added,
   * if we upload 5 files at once, onChange will be triggered 5 times,
   * so we have to use debounce to combine files to 1 time
   */
  const handleChange = (event) => {
    const status = event?.file?.status;

    if (status === "removed") {
      handleDelete(event?.file?.uid);
      return;
    }

    if (status === "uploading") {
      debouncedHandleUploading(event);
      return;
    }
  };

  const debouncedHandleUploading = useMemo(
    () =>
      debounce(async (event) => {
        setIsOpenUploadModal(true);

        const uploadingFiles = event.fileList.filter(
          (file) => file?.status === "uploading",
        );

        if (!uploadingFiles.length) {
          setIsOpenUploadModal(false);
          return;
        }

        const totalSizeUpload = uploadingFiles.reduce(
          (sum, file) => sum + (file?.size || 0),
          0,
        );

        if (totalSizeUpload > MAX_FILE_SIZE) {
          openNotificationError("File size exceeds the 1GB limit");
          setIsOpenUploadModal(false);
          return;
        }

        const filesArray = uploadingFiles.map((file) => {
          const originFile = file?.originFileObj;
          originFile.uid = uuidv4();
          return originFile;
        });

        try {
          const encodeFiles = await Promise.all(
            filesArray.map(
              (file) =>
                new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  reader.onloadend = () => resolve(file);
                  reader.onerror = reject;
                }),
            ),
          );

          setUploadFiles((prev) => [...prev, ...encodeFiles]);
        } catch (error) {
          console.error("Read file error:", error);
        }
      }, 200),
    [],
  );

  const handleDelete = (fileKey) => {
    if (!fileKey) return;

    const newFiles = previewFiles?.filter(
      (file) => file?.mainPath !== fileKey && file?.uid !== fileKey,
    );
    setPreviewFiles(newFiles);
  };

  const removeFormData = () => {
    setPreviewFiles([]);
    setFileList([]);
  };

  const handleCustomRequest = ({ file, onSuccess, onError }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const handleDragOver = (e) => {
    // Prevent the default behavior to avoid file duplication
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    return () => debouncedHandleUploading.cancel();
  }, [debouncedHandleUploading]);

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between">
        <div className="font-bold">
          {previewFiles?.length}{" "}
          {languageMap?.["filePreview.fileTotal"] ?? "file"}
          {user?.language === "us" && previewFiles?.length > 1 && "s"}
        </div>
        <button className="remove-all" onClick={removeFormData}>
          {languageMap?.["filePreview.removeAll"] ?? "Remove all"}
        </button>
      </div>
      <div
        className="flex max-h-[190px] overflow-y-scroll mt-[10px] gap-[10px]"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {previewFiles?.length > 0 ? (
          <Upload
            listType="picture-card"
            fileList={previewFiles}
            onPreview={handlePreview}
            onChange={handleChange}
            customRequest={handleCustomRequest}
            multiple
            itemRender={(originNode, file) => (
              <UploadedFilePreview
                originNode={originNode}
                file={file}
                onDelete={handleDelete}
              />
            )}
          >
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center">
                <PlusOutlined />
                <span className="mt-1">Upload</span>
              </div>
            </div>
          </Upload>
        ) : (
          <Upload
            listType="picture-card"
            fileList={fileList}
            onPreview={handlePreview}
            onChange={handleChange}
            customRequest={handleCustomRequest}
            multiple
          >
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center">
                <PlusOutlined />
                <span className="mt-1">Upload</span>
              </div>
            </div>
          </Upload>
        )}
        {previewImage && (
          <Image
            wrapperStyle={{
              display: "none",
            }}
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage(""),
            }}
            src={previewImage}
          />
        )}
      </div>
    </div>
  );
};

export { MailFilesPreview };
